import { Icon } from '@iconify/react';
import { Column } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

const DEFAULT_VISIBLE_OPTIONS_CAP = 50;
const SHOW_MORE_STEP = 50;

type Props<TData, TValue> = {
  column?: Column<TData, TValue>;
  title?: string;
  options: {
    label: string;
    value: string;
    icon?: React.ComponentType<{ className?: string }>;
  }[];
  emptyState?: ReactNode;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  searchQuery?: string;
  onSearchQueryChange?: (value: string) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  visibleOptionsCap?: number;
  showMoreLabel?: string;
};

export const DataTableFacetedFilter = <TData, TValue>({
  column,
  title,
  options,
  emptyState,
  enableSearch = false,
  searchPlaceholder = 'Search...',
  searchQuery,
  onSearchQueryChange,
  open,
  onOpenChange,
  visibleOptionsCap = DEFAULT_VISIBLE_OPTIONS_CAP,
  showMoreLabel,
}: Props<TData, TValue>) => {
  const facets = column?.getFacetedUniqueValues();
  const selectedValues = new Set(column?.getFilterValue() as string[]);

  const [internalQuery, setInternalQuery] = useState('');
  const [internalOpen, setInternalOpen] = useState(false);
  const [visibleCap, setVisibleCap] = useState(visibleOptionsCap);

  const query = searchQuery ?? internalQuery;
  const isControlledSearch = onSearchQueryChange !== undefined;
  const setQuery = isControlledSearch ? onSearchQueryChange : setInternalQuery;
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;

  const filteredOptions = useMemo(() => {
    if (!enableSearch || isControlledSearch || !query.trim()) {
      return options;
    }
    const lowered = query.toLowerCase();
    return options.filter((option) => option.label.toLowerCase().includes(lowered));
  }, [enableSearch, isControlledSearch, options, query]);

  const visibleOptions = filteredOptions.slice(0, visibleCap);
  const hiddenCount = filteredOptions.length - visibleOptions.length;

  const handleCheckedChange = (optionValue: string, checked: boolean) => {
    const updatedValues = new Set(selectedValues);
    if (checked) {
      updatedValues.add(optionValue);
    } else {
      updatedValues.delete(optionValue);
    }

    const filterValues = Array.from(updatedValues);
    column?.setFilterValue(filterValues.length ? filterValues : undefined);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) {
      setVisibleCap(visibleOptionsCap);
    }
  };

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm" className="!border-dashed hover:bg-accent">
          <Icon icon="mingcute:filter-2-line" className="h-4 w-4" />
          {title}
          {selectedValues?.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-1 h-4" />
              <Badge variant="secondary" className="rounded-md px-1 font-normal lg:hidden">
                {selectedValues.size}
              </Badge>
              <div className="hidden space-x-1 lg:flex">
                {selectedValues.size > 2 ? (
                  <Badge variant="secondary" className="rounded-md font-normal">
                    {selectedValues.size} selected
                  </Badge>
                ) : (
                  options
                    .filter((option) => selectedValues.has(option.value))
                    .map((option) => (
                      <Badge
                        variant="secondary"
                        key={option.value}
                        className="rounded-md font-normal"
                      >
                        {option.label}
                      </Badge>
                    ))
                )}
              </div>
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[260px] custom-scroll"
        align="start"
        side="bottom"
        avoidCollisions={false}
      >
        <DropdownMenuLabel>{title}</DropdownMenuLabel>
        {enableSearch && (
          <div className="px-1 pb-1">
            <Input
              size="sm"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => event.stopPropagation()}
              placeholder={searchPlaceholder}
              startIcon={<Icon icon="mingcute:search-line" className="h-4 w-4" />}
            />
          </div>
        )}
        <DropdownMenuSeparator />
        <div className="max-h-64 overflow-y-auto">
          {visibleOptions.length > 0 ? (
            <>
              {visibleOptions.map((option) => {
                const isSelected = selectedValues.has(option.value);
                return (
                  <DropdownMenuCheckboxItem
                    key={option.value}
                    checked={isSelected}
                    onCheckedChange={(checked) => handleCheckedChange(option.value, checked)}
                    onSelect={(e) => e.preventDefault()}
                    className="flex items-center gap-2 truncate"
                  >
                    {option.icon && <option.icon className="mr-2 h-4 w-4 text-muted-foreground" />}
                    <span className="flex-1 truncate" title={option.label}>
                      {option.label}
                    </span>
                    {facets?.get(option.value) && (
                      <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                        {facets.get(option.value)}
                      </span>
                    )}
                  </DropdownMenuCheckboxItem>
                );
              })}
              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setVisibleCap((cap) => cap + SHOW_MORE_STEP)}
                  className="w-full px-3 py-2 text-left text-sm text-muted-foreground hover:bg-accent"
                  data-testid="faceted-filter-show-more"
                >
                  {showMoreLabel ??
                    `Show ${Math.min(SHOW_MORE_STEP, hiddenCount)} more (${hiddenCount} hidden)`}
                </button>
              )}
            </>
          ) : (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {emptyState ?? 'No options available'}
            </div>
          )}
        </div>
        {selectedValues.size > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              onCheckedChange={() => column?.setFilterValue(undefined)}
              className="justify-center text-center"
              onSelect={(e) => e.preventDefault()}
            >
              Clear filters
            </DropdownMenuCheckboxItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
